import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/Button'
import { AttachmentPreviewModal } from './AttachmentPreviewModal'
import { useAuth } from '../auth/AuthContext'
import { attachmentsApi } from '../api/attachments.api'
import { toast } from '../store/toastStore'
import { ApiError } from '../api/client'
import type { Attachment } from '../api/types'

const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024
const THUMBNAIL_SIZE = 36

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImage(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

function AttachmentThumbnail({ attachment, onClick }: { attachment: Attachment; onClick: () => void }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    fetch(attachmentsApi.downloadUrl(attachment.id), { credentials: 'include' })
      .then((res) => res.blob())
      .then((blob) => {
        if (cancelled) return
        url = URL.createObjectURL(blob)
        setObjectUrl(url)
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [attachment.id])

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${attachment.originalName} als Vorschau öffnen`}
      style={{
        width: THUMBNAIL_SIZE,
        height: THUMBNAIL_SIZE,
        flexShrink: 0,
        padding: 0,
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--radius-sm)',
        background: objectUrl ? `center/cover url(${objectUrl})` : 'var(--surface-3)',
        cursor: 'pointer',
      }}
    />
  )
}

interface AttachmentListProps {
  parentType: 'task' | 'document'
  parentId: string
}

/** Datei-Anhänge für eine Aufgabe oder einen Dokumentations-Eintrag — self-contained wie
 * LabelPicker, holt Daten und prüft Rechte selbst statt sie vom Parent durchgereicht zu
 * bekommen. */
export function AttachmentList({ parentType, parentId }: AttachmentListProps) {
  const queryClient = useQueryClient()
  const { hasAnyPermission } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null)

  const canEdit = hasAnyPermission([parentType === 'task' ? 'tasks.edit' : 'documentation.edit'])
  const queryKey = ['attachments', parentType, parentId]

  const listQuery = useQuery({
    queryKey,
    queryFn: async () => (await (parentType === 'task' ? attachmentsApi.listForTask(parentId) : attachmentsApi.listForDocument(parentId))).data,
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => (parentType === 'task' ? attachmentsApi.uploadForTask(parentId, file) : attachmentsApi.uploadForDocument(parentId, file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success('Datei hochgeladen.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Upload fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) => attachmentsApi.remove(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      toast.success('Anhang gelöscht.')
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Löschen fehlgeschlagen.'),
  })

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      toast.error('Datei ist zu groß (max. 20 MB).')
      return
    }
    uploadMutation.mutate(file)
  }

  return (
    <div>
      {canEdit ? (
        <>
          <input ref={fileInputRef} type="file" onChange={onFileChange} style={{ display: 'none' }} />
          <Button
            size="sm"
            variant="secondary"
            loading={uploadMutation.isPending}
            onClick={() => fileInputRef.current?.click()}
            style={{ marginBottom: 'var(--space-2)' }}
          >
            + Datei anhängen
          </Button>
        </>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {listQuery.data?.map((a) => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: '6px 10px',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {isImage(a.mimeType) ? <AttachmentThumbnail attachment={a} onClick={() => setPreviewAttachment(a)} /> : null}
            {isImage(a.mimeType) ? (
              <button
                type="button"
                onClick={() => setPreviewAttachment(a)}
                style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none', padding: 0, color: 'var(--accent-10)', cursor: 'pointer', font: 'inherit', wordBreak: 'break-all' }}
              >
                {a.originalName}
              </button>
            ) : (
              <span style={{ flex: 1, wordBreak: 'break-all' }}>{a.originalName}</span>
            )}
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>{formatBytes(a.size)}</span>
            <a
              href={attachmentsApi.downloadUrl(a.id)}
              aria-label={`${a.originalName} herunterladen`}
              style={{ color: 'var(--text-muted)', display: 'flex' }}
            >
              ⬇
            </a>
            {canEdit ? (
              <button
                onClick={() => deleteMutation.mutate(a.id)}
                aria-label="Anhang löschen"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                ✕
              </button>
            ) : null}
          </div>
        ))}
        {listQuery.data?.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>Keine Anhänge.</span> : null}
      </div>

      {previewAttachment ? <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} /> : null}
    </div>
  )
}
